import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Home from '../views/Home.vue';
import store from '@/store';
import RouterUtil from '@/utils/router.util';
import routesDashboards from './routesDashboards';
import { middlewareAddDashboard } from './middleware/dashboardRoutes.middleware';

Vue.use(VueRouter);

const Login = () =>
  import(/* webpackChunkName: "Login" */ '@/views/Login/Login.vue');

const UserNotAuthorized = () =>
  import(
    /* webpackChunkName: "UserNotAuthorized" */ '@/views/Login/UserNotAuthorized.vue'
  );

const EmptyRouterView = () =>
  import(
    /* webpackChunkName: "EmptyRouterView" */ '@/components/EmptyRouterView.vue'
  );

const Users = () =>
  import(/* webpackChunkName: "Users" */ '@/views/Admin/Users/Users.vue');

const NotFound = () =>
  import(/* webpackChunkName: "NotFound" */ '@/views/NotFound.vue');

const Groups = () =>
  import(/* webpackChunkName: "Groups" */ '@/views/Admin/Groups/Groups.vue');

const Logout = () =>
  import(/* webpackChunkName: "Logout" */ '@/views/Login/Logout.vue');

const HomeSettings = () =>
  import(
    /* webpackChunkName: "HomeSettings" */ '@/views/Admin/HomeSettings/homeSettings.vue'
  );

const NPS = () => import(/* webpackChunkName: "NPS" */ '@/views/NPS/Nps.vue');

const NpsInsightSettings = () =>
  import(
    /* webpackChunkName: "NpsInsightSettings" */ '@/views/Admin/NpsInsightSettings/NpsInsightSettings.vue'
  );

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'home',
    meta: {
      hiddenOnNavigation: true,
    },
    component: Home,
  },
  {
    path: '/admin',
    name: 'admin',
    component: EmptyRouterView,
    redirect: '/admin/usuarios',
    meta: {
      labels: ['Administração'],
    },
    children: [
      routesDashboards,
      {
        path: 'regras',
        name: 'groups',
        component: Groups,
        meta: {
          labels: ['Regras'],
          customColors: ['#FF7A13', '#FF2C44', '#FF1B7B', '#AD22D7'],
          requiresPermission: 'isPermissionManager',
        },
      },
      {
        path: 'usuarios',
        name: 'users',
        component: Users,
        meta: {
          labels: ['Usuários'],
          customColors: ['#FF7A13', '#FF2C44', '#FF1B7B', '#AD22D7'],
          requiresPermission: 'isPermissionManager',
        },
      },
      {
        path: 'configuracoes-home',
        name: 'homeSettings',
        component: HomeSettings,
        meta: {
          labels: ['Configurações da Home'],
          customColors: ['#FF7A13', '#FF2C44', '#FF1B7B', '#AD22D7'],
          requiresPermission: 'isPermissionManager',
        },
      },
      {
        path: 'configuracoes-insights-nps',
        name: 'NpsInsightSettings',
        component: NpsInsightSettings,
        meta: {
          labels: ['Configurações de Insights do NPS  '],
          customColors: ['#FF7A13', '#FF2C44', '#FF1B7B', '#AD22D7'],
          requiresPermission: 'isPermissionManager',
        },
      },
    ],
  },
  //Login
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      labels: ['Login'],
      hiddenOnNavigation: true,
      customColors: ['#02EBFE', '#1297DF', '#6C66ED'],
    },
  },
  {
    path: '/logout',
    name: 'logout',
    component: Logout,
    meta: {
      hiddenOnNavigation: true,
      customColors: ['#02EBFE', '#1297DF', '#6C66ED'],
    },
  },
  // Generic routes
  {
    path: '/nao-autorizado',
    name: 'userNotAuthorized',
    component: UserNotAuthorized,
    meta: {
      hiddenOnNavigation: true,
    },
  },
  {
    path: '*',
    name: 'notFound',
    component: NotFound,
    meta: {
      hiddenOnNavigation: true,
    },
  },
  // nps
  {
    path: '/nps',
    name: 'nps',
    component: NPS,
    meta: {
      labels: ['NPS | Acompanhamento da meta executiva'],
      description:
        'NPS: Net Promoter Score, índice calculado pela diferença entre promotores e detratores.\nFonte: Pesquisa Masterbrand do Sistema de Monitoramento de Marcas, realizado pela Kantar Insighs para Globo. Pesquisa quantitativa online, com 800 casos mensais, refletindo pop. brasileira 18-65 ABC\nContém os principais resultados de NPS e indicadores de reputação e saúde de marcas relacionados. ',
      customColors: ['#02EBFE', '#1297DF', '#6C66ED'],
      requiresPermission: 'isNpsUser',
      hiddenToNotAllowedUsers: true,
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

const gtag_id = process.env.VUE_APP_GTAG_ID;

router.beforeEach(async (to, from, next) => {
  const { isLogged, currentUser } = store.state.user;
  const isUnprotectedRoute = (name: string | null | undefined) => {
    if (name) return ['login', 'logout', 'userNotAuthorized'].includes(name);
    return false;
  };

  if (isUnprotectedRoute(to.name)) {
    next();
  } else {
    if (!isLogged) {
      next({
        name: 'login',
        query: {
          redirectTo: to.fullPath,
        },
      });
      return;
    }

    if (isLogged && currentUser === undefined) {
      await store.dispatch('authorize');
    }
    const routesWereRegistered = await middlewareAddDashboard(router);
    if (routesWereRegistered) {
      next({ path: to.fullPath, replace: true });
      return;
    }
    if (gtag_id && isLogged && store.state.user.currentUser?.email) {
      Vue.prototype.$gtag.set('user_properties', {
        email: store.state.user.currentUser.email,
        department: store.state.user.currentUser.department,
        position: store.state.user.currentUser.position,
        hierarchy: JSON.stringify(store.state.user.currentUser.hierarchy),
        responsibilityTerm:
          store.state.user.currentUser.isResponsibilityTermSigned,
      });
    }
    RouterUtil.isRouteAllowed(to, store.state.user.currentUser)
      ? next()
      : next({ name: 'userNotAuthorized' });
  }
});

export default router;
